/**
 * Staff sign-in — task 31, closing the gap task 27 flagged: services/auth has
 * a fully-built, reviewed staff sign-in (email plus a one-time code,
 * restricted to the configured staff domain, auto-provisioning an in-domain
 * address as a plain tech), but nothing in the console ever showed a screen
 * for it, so nobody could actually sign in.
 *
 * Same OTP primitive apps/client/src/screens/SignInEmail.tsx uses for the
 * client sign-in path (services/auth's one otp.ts, task-9-report.md) — this
 * is not a second implementation of email+code, it is the staff-domain-
 * restricted pair of endpoints (src/lib/staffAuth.ts) against that same
 * primitive.
 *
 * Deliberately NOT tier-shaped: the five-tier client confidence model
 * (`ThreadSummary.tier`, `assertGate`) does not apply to staff
 * (communicatorv2.3.md §5) — this screen shares no badge, no gate copy, no
 * vocabulary with that axis. Staff identity is a separate question entirely:
 * signed in or not, plus role and admin, both read fresh from the `staff`
 * table on every request (services/ops/src/routes/whoami.ts).
 *
 * The staff email domain is never a literal here (global constraint) — it
 * comes from services/auth's own config (`STAFF_EMAIL_DOMAIN`) and only ever
 * reaches this screen inside a server-produced refusal message
 * ("not eligible for staff sign-in — use your @<domain> address"), so the
 * generic copy below stays domain-agnostic and the concrete domain shows up
 * exactly when it matters: when someone actually tries an out-of-domain
 * address.
 *
 * Entra (§5's end state — SSO against the operating MSP's own tenant, plain,
 * no client-identity tiering) is now offered here too, as a full-page
 * navigation to services/auth's own start route. Which methods appear is read
 * from `GET /auth/methods`, never hardcoded: a deployment with no Entra block
 * configured shows no Microsoft button at all. Email plus a code stays
 * exactly as it was — it is the fallback when Entra is misconfigured, and the
 * same OTP primitive the client side needs regardless.
 *
 * Both methods converge on the same ending: the session cookies are already
 * set (by the verify response, or by the Entra callback), then
 * `identityClient.whoAmI()` + `setAuthStatus('signed-in')`. Nothing
 * downstream of that call (AuthGate, the store, ownership comparisons) knows
 * or cares which front end was used.
 */
import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import {
  staffAuth as defaultStaffAuthClient,
  ENTRA_ERROR_PARAM,
  FALLBACK_SIGN_IN_METHODS,
  type createStaffAuthClient,
  type StaffSignInMethods,
} from '../lib/staffAuth'
import { identity as defaultIdentityClient } from '../lib/identity'
import { take as takeIntendedDestination } from '../lib/intendedDestination'
import type { StaffIdentity } from '../lib/identity'
import { useStore } from '../store'

/**
 * README §8's exact tagline, "One pane. Every channel." — original design
 * copy (no verbatim source elsewhere), kept as one named constant instead of
 * inline so it reads as intentional rather than a stray string.
 */
const TAGLINE = 'One pane. Every channel.'

type StaffAuthClientMethods = ReturnType<typeof createStaffAuthClient>
/**
 * `signInMethods`/`entraStartUrl` are optional so a caller (or a test) built
 * against the code-only client keeps working: an absent `signInMethods`
 * simply means "email plus a code only", which is the safe reading.
 */
type StaffAuthClient = Pick<StaffAuthClientMethods, 'requestStaffCode' | 'verifyStaffCode'> &
  Partial<Pick<StaffAuthClientMethods, 'signInMethods' | 'entraStartUrl'>>

interface IdentityClient {
  /** `'unreachable'` means the server could not answer — see identity.ts. */
  whoAmI: () => Promise<StaffIdentity | null | 'unreachable'>
}

/** Where a completed Entra sign-in lands — the console's root, not a named route. */
const HOME_PATH = '/'

/**
 * Plain-language copy for services/auth's own refusal codes. The screen never
 * shows a bare code with no explanation, and never names the staff email
 * domain (it has no way to know it — see the header comment).
 */
function entraErrorMessage(code: string): string {
  if (code === 'out_of_domain') {
    return 'That Microsoft account is not on the team’s own email domain, so it cannot sign in here. Use your work account, or sign in with a code below.'
  }
  if (code.startsWith('provider_')) {
    return 'Microsoft did not complete that sign-in. Try again, or sign in with a code below.'
  }
  return `Microsoft sign-in could not be verified (${code}). Try again, or sign in with a code below.`
}

/** Reads a refused Entra sign-in's reason out of the current URL, if there is one. */
function initialEntraError(): string | null {
  if (typeof window === 'undefined') return null
  const code = new URLSearchParams(window.location.search).get(ENTRA_ERROR_PARAM)
  return code ? entraErrorMessage(code) : null
}

export interface SignInPageProps {
  /** Defaults to the real singleton; tests inject a fake to skip the network. */
  staffAuthClient?: StaffAuthClient
  /** Defaults to the real singleton. Called once verify succeeds to seed the full record (email/covering) the verify response itself does not carry. */
  identityClient?: IdentityClient
  /**
   * How the browser leaves for the identity provider. Defaults to a
   * whole-page navigation, which is what makes this the system browser rather
   * than a webview (v2.3 §4). Injected only so a test can assert where it
   * would have gone.
   */
  navigateTo?: (url: string) => void
}

type Step = 'email' | 'code'

export function SignInPage({
  staffAuthClient = defaultStaffAuthClient,
  identityClient = defaultIdentityClient,
  navigateTo,
}: SignInPageProps) {
  const [step, setStep] = useState<Step>('email')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(initialEntraError)
  const [busy, setBusy] = useState(false)
  const [methods, setMethods] = useState<StaffSignInMethods>(FALLBACK_SIGN_IN_METHODS)
  const setMe = useStore((s) => s.setMe)
  const setAuthStatus = useStore((s) => s.setAuthStatus)
  const set = useStore((s) => s.set)
  // Same runtime-configured product name Header.tsx/App.tsx read, and the
  // same "Console" fallback — never a literal (deploy/test/no-literals.test.mjs
  // forbids hardcoding the actual default product name anywhere). `cfg` is
  // realistically still empty here: `GET /ops/config` requires an
  // authenticated staff actor (bootstrap.ts), so a fresh, signed-out load
  // shows the fallback until sign-in completes and the app re-renders past
  // this screen — an honest generic name beats fabricating one.
  const nameFromCfg = useStore((s) => s.cfg['b.name'])
  const wordmark = typeof nameFromCfg === 'string' && nameFromCfg.length > 0 ? nameFromCfg : 'Console'

  useEffect(() => {
    let live = true
    const read = staffAuthClient.signInMethods
    if (!read) return
    void read().then((next) => {
      if (live) setMethods(next)
    })
    return () => {
      live = false
    }
  }, [staffAuthClient])

  async function handleEntra() {
    const build = staffAuthClient.entraStartUrl
    if (!build || busy) return
    setBusy(true)
    try {
      // errorTo is simply where we already are: a refusal comes back to this
      // same screen, which is what lets it say why in plain language.
      // Back to what they clicked, not to the front page. Entra leaves this
      // page entirely, so the destination has to travel with the flow —
      // services/auth's `safeReturnPath` accepts any same-origin path,
      // query included, and refuses anything else.
      //
      // Consumed here rather than peeked: if this sign-in is abandoned, a
      // destination left lying in storage would be replayed by an unrelated
      // sign-in later.
      const url = await build(takeIntendedDestination() ?? HOME_PATH, window.location.pathname)
      const go = navigateTo ?? ((next: string) => window.location.assign(next))
      go(url)
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Could not reach Microsoft sign-in. Sign in with a code instead.',
      )
      setBusy(false)
    }
  }

  async function handleSendCode(e: FormEvent) {
    e.preventDefault()
    const trimmed = email.trim()
    if (!trimmed || busy) return
    setError(null)
    setBusy(true)
    try {
      await staffAuthClient.requestStaffCode(trimmed)
      setStep('code')
    } catch (err) {
      // The out-of-domain refusal ("not eligible for staff sign-in — use
      // your @<domain> address") and the rate-limit message both come
      // straight from services/auth (src/lib/staffAuth.ts) — shown verbatim,
      // never replaced with a generic "something went wrong."
      setError(err instanceof Error ? err.message : 'Could not send the code. Try again.')
    } finally {
      setBusy(false)
    }
  }

  async function handleVerify(e: FormEvent) {
    e.preventDefault()
    if (code.length !== 6 || busy) return
    setError(null)
    setBusy(true)
    try {
      await staffAuthClient.verifyStaffCode(email.trim(), code)
      // The verify response carries id/name/role/admin only; one more read
      // gets the full record (email, covering) now that the session cookie
      // the identity read needs is in place.
      const me = await identityClient.whoAmI()
      // Just signed in, so the session IS good. An unreachable read here is a
      // blip, not a refusal — treat it as "no extra detail yet" rather than
      // bouncing someone back to the form they have already completed.
      if (me && me !== 'unreachable') {
        setMe(me)
        set({ role: me.role, admin: me.admin, superAdmin: me.superAdmin ?? false })
      }
      setAuthStatus('signed-in')
      // No explicit navigation call here — src/components/auth/AuthGate.tsx's
      // RedirectIfSignedIn is what's mounted around this page right now (we
      // are on /sign-in) and reacts to authStatus flipping on its own. See
      // that file's own header comment.
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That code did not work. Try again.')
    } finally {
      setBusy(false)
    }
  }

  // README §8: a fixed navy-gradient panel with a white lockup above a white
  // card — see tokens.css's own comment on the --signin-* tokens for why
  // this screen is theme-independent rather than themed (no preference is
  // known yet, and the design gives one literal set with no dark variant).
  // The primary/secondary buttons below are plain-styled rather than the
  // shared `Button` component: `Button`'s variant classes reach for
  // theme-dependent tokens (`bg-elevated`, `border-subtle`, `text-hi`),
  // which is exactly correct everywhere else in this app but wrong here —
  // this card does not follow the active theme.
  const primaryBtn =
    'inline-flex w-full items-center justify-center rounded-control bg-signin-primary py-3 text-[13px] font-medium text-white transition-colors hover:bg-signin-primary-hover disabled:cursor-not-allowed disabled:opacity-50'
  const secondaryBtn =
    'inline-flex w-full items-center justify-center rounded-control border border-signin bg-signin-card py-3 text-[13px] font-medium text-signin-heading transition-colors hover:bg-signin-field disabled:cursor-not-allowed disabled:opacity-50'
  const fieldInput =
    'w-full rounded-[10px] border border-signin bg-signin-field px-3 py-3 text-[14px] text-signin-heading outline-none placeholder:text-signin-muted'

  return (
    <div
      data-testid="sign-in-page"
      className="flex min-h-screen flex-col items-center justify-center bg-gradient-night px-4"
    >
      <div className="mb-6 flex flex-col items-center gap-2">
        <img src="/brand-mark.svg" alt="" aria-hidden="true" width={40} height={40} className="flex-none" />
        <span className="font-display text-[19px] font-semibold tracking-[-0.04em] text-white">{wordmark}</span>
      </div>
      <div className="w-full max-w-[380px] rounded-[16px] bg-signin-card p-7 shadow-signin">
        {step === 'code' ? (
          <>
            <h1 className="font-display text-[21px] font-semibold leading-tight text-signin-heading">
              Enter the code
            </h1>
            <p className="mt-2 text-[13px] leading-relaxed text-signin-body">
              We sent a six-digit code to <span className="text-signin-heading">{email.trim()}</span> — the
              address you just entered, not any other address on file.
            </p>

            <form onSubmit={handleVerify} className="mt-4 flex flex-col gap-4">
              <input
                aria-label="6-digit code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                inputMode="numeric"
                autoComplete="one-time-code"
                className={`${fieldInput} text-center font-mono text-[22px] font-semibold tracking-[0.3em]`}
              />

              {error ? (
                <p role="alert" className="rounded-[8px] bg-signin-danger-soft px-3 py-2 text-[11px] leading-relaxed text-signin-danger">
                  {error}
                </p>
              ) : null}

              <button type="submit" disabled={code.length !== 6 || busy} className={primaryBtn}>
                Verify and sign in
              </button>
            </form>

            <button
              type="button"
              onClick={() => {
                setStep('email')
                setCode('')
                setError(null)
              }}
              className="mt-4 text-left text-[11px] text-signin-muted"
            >
              Use a different email
            </button>
          </>
        ) : (
          <>
            <h1 className="font-display text-[21px] font-semibold leading-tight text-signin-heading">Sign in</h1>
            <p className="mt-2 text-[13px] leading-relaxed text-signin-body">
              Sign in with your work email — sign-in is restricted to the team&rsquo;s own email
              domain, so a personal address will not work. We&rsquo;ll send a six-digit code
              straight to the address you enter below.
            </p>

            {methods.entra ? (
              <>
                <button type="button" onClick={handleEntra} disabled={busy} className={`mt-4 ${secondaryBtn}`}>
                  Sign in with Microsoft
                </button>
                <div className="mt-4 flex items-center gap-3" aria-hidden="true">
                  <span className="flex-1 border-t border-signin" />
                  <span className="text-[10px] uppercase tracking-wide text-signin-muted">or</span>
                  <span className="flex-1 border-t border-signin" />
                </div>
              </>
            ) : null}

            <form onSubmit={handleSendCode} className="mt-4 flex flex-col gap-4">
              <div>
                <label
                  htmlFor="staff-signin-email"
                  className="mb-1.5 block text-[10px] font-semibold uppercase tracking-wide text-signin-muted"
                >
                  Work email
                </label>
                <input
                  id="staff-signin-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@yourcompany.com"
                  className={fieldInput}
                />
              </div>

              {error ? (
                <p role="alert" className="rounded-[8px] bg-signin-danger-soft px-3 py-2 text-[11px] leading-relaxed text-signin-danger">
                  {error}
                </p>
              ) : null}

              <button type="submit" disabled={!email.trim() || busy} className={primaryBtn}>
                Send me a code
              </button>
            </form>
          </>
        )}
      </div>
      <p className="mt-6 text-[12px] text-signin-tagline">{TAGLINE}</p>
    </div>
  )
}
